import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["banner", "settingsModal", "necessarySwitch", "analyticsSwitch", "marketingSwitch"]
  static values = {
    requiresConsent: Boolean
  }

  connect() {
    this.initializeCookieConsent()

    // Listen for global events
    window.addEventListener('showCookieSettings', this.handleShowSettings.bind(this))
  }

  disconnect() {
    window.removeEventListener('showCookieSettings', this.handleShowSettings.bind(this))
  }

  initializeCookieConsent() {
    // Check if user needs to see consent (based on geo-location or previous consent)
    if (this.requiresConsentValue && !this.hasExistingConsent()) {
      this.showBanner()
    } else if (this.hasExistingConsent()) {
      this.loadConsentedServices()
    } else {
      // For non-EU users, load all services by default but still provide opt-out
      this.setDefaultConsent()
      this.loadConsentedServices()
    }
  }

  hasExistingConsent() {
    return localStorage.getItem('cookie-consent') !== null
  }

  getConsentData() {
    const defaultConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }

    const stored = localStorage.getItem('cookie-consent')
    return stored ? JSON.parse(stored) : defaultConsent
  }

  setDefaultConsent() {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }
    localStorage.setItem('cookie-consent', JSON.stringify(consent))
    this.updateConsentSignals(consent)
  }

  saveConsent(consentData) {
    localStorage.setItem('cookie-consent', JSON.stringify({
      ...consentData,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }))

    // Trigger consent change event
    window.dispatchEvent(new CustomEvent('consentChanged', {
      detail: consentData
    }))
  }

  loadConsentedServices() {
    const consent = this.getConsentData()
    this.updateConsentSignals(consent)

    if (consent.analytics) {
      this.loadAhrefsAnalytics()
    }

    this.updateConsentSwitches(consent)
  }

  updateConsentSignals(consent) {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        ad_storage: consent.marketing ? 'granted' : 'denied',
        ad_user_data: consent.marketing ? 'granted' : 'denied',
        ad_personalization: consent.marketing ? 'granted' : 'denied'
      })
    }
  }

  loadAhrefsAnalytics() {
    if (!document.querySelector('script[data-key="3ejGm9AYEJykyCfshXHofg"]')) {
      const script = document.createElement('script')
      script.src = 'https://analytics.ahrefs.com/analytics.js'
      script.setAttribute('data-key', '3ejGm9AYEJykyCfshXHofg')
      script.async = true
      document.body.appendChild(script)
    }
  }

  updateConsentSwitches(consent) {
    if (this.hasNecessarySwitchTarget) {
      this.necessarySwitchTarget.checked = consent.necessary
    }
    if (this.hasAnalyticsSwitchTarget) {
      this.analyticsSwitchTarget.checked = consent.analytics
    }
    if (this.hasMarketingSwitchTarget) {
      this.marketingSwitchTarget.checked = consent.marketing
    }
  }

  // Banner Actions
  showBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove('hidden')
      this.bannerTarget.classList.add('show')
    }
  }

  hideBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove('show')
      this.bannerTarget.classList.add('hidden')
    }
  }

  acceptAll() {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: false
    }

    this.saveConsent(consent)
    this.loadConsentedServices()
    this.hideBanner()
  }

  acceptNecessary() {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false
    }

    this.saveConsent(consent)
    this.updateConsentSignals(consent)
    this.hideBanner()
  }

  // Settings Modal Actions
  showSettings() {
    if (this.hasSettingsModalTarget) {
      const consent = this.getConsentData()
      this.updateConsentSwitches(consent)
      this.settingsModalTarget.classList.remove('hidden')
      this.settingsModalTarget.classList.add('show')
    }
  }

  hideSettings() {
    if (this.hasSettingsModalTarget) {
      this.settingsModalTarget.classList.remove('show')
      this.settingsModalTarget.classList.add('hidden')
    }
  }

  // Handle external events (like from footer button)
  handleShowSettings(event) {
    this.showSettings()
  }

  saveSettings() {
    const consent = {
      necessary: this.hasNecessarySwitchTarget ? this.necessarySwitchTarget.checked : true,
      analytics: this.hasAnalyticsSwitchTarget ? this.analyticsSwitchTarget.checked : false,
      marketing: this.hasMarketingSwitchTarget ? this.marketingSwitchTarget.checked : false
    }

    this.saveConsent(consent)
    this.hideSettings()

    // Reload page to apply new consent settings
    window.location.reload()
  }

  // Cookie Management
  clearAllCookies() {
    // Clear all non-necessary cookies
    const cookies = document.cookie.split(";")

    for (let cookie of cookies) {
      const eqPos = cookie.indexOf("=")
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()

      // Don't clear necessary cookies
      if (!this.isNecessaryCookie(name)) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`
      }
    }

    // Clear localStorage items (except consent)
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key !== 'cookie-consent') {
        localStorage.removeItem(key)
      }
    }

    // Clear sessionStorage
    sessionStorage.clear()
  }

  isNecessaryCookie(name) {
    const necessaryCookies = [
      '_session',
      'csrf_token',
      'flash',
      'cookie-consent'
    ]

    return necessaryCookies.some(necessary => name.includes(necessary))
  }

}